import React from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowRight } from "lucide-react";

export default function Vilkar() {
  return (
    <div data-testid="page-vilkar">
      {/* Hero */}
      <section className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#D97B66]"></span>
            <span className="text-xs uppercase tracking-[0.25em] text-[#6B726C] font-medium">
              Juridisk
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-[#2C302E] leading-[1.05]">
            Handelsvilkår
          </h1>
          <p className="mt-6 text-base text-[#6B726C]">
            <strong>Sist oppdatert: 1. juni 2026</strong>
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
        <div className="space-y-10 text-[#2C302E]">

          <div className="flex items-start gap-4 p-6 bg-[#D97B66]/10 rounded-2xl">
            <FileText className="w-6 h-6 text-[#D97B66] mt-1 flex-shrink-0" strokeWidth={1.5} />
            <p className="text-sm text-[#2C302E] leading-relaxed m-0">
              Disse vilkårene gjelder for alle kjøp av digitale produkter (PDF-filer) fra Småstund.
              Ved å fullføre et kjøp aksepterer du disse vilkårene.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">1. Generelt</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              Disse vilkårene gjelder for alle kjøp av digitale produkter (PDF-filer) fra Småstund.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">2. Priser</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A]">
              <li>Alle priser er i norske kroner (NOK) inkludert mva.</li>
              <li>Vi forbeholder oss retten til å endre priser uten varsel</li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">3. Betaling</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A]">
              <li>Vi aksepterer betaling via Stripe (kort, Vipps, Klarna – avhengig av din bank)</li>
              <li>Betaling trekkes når ordren bekreftes</li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">4. Levering av digitale produkter</h2>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A]">
              <li>PDF-filene sendes <strong>automatisk</strong> til din e-post umiddelbart etter betalt ordre</li>
              <li>Du får også en nedlastingslenke på ordrebekreftelsessiden</li>
              <li>Leveringstid: 0–5 minutter</li>
            </ul>
            <p className="text-[#4A4F4A] leading-relaxed mt-4">
              <strong>Hvis du ikke mottar e-posten:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A] mt-2">
              <li>Sjekk søppelpostmappen</li>
              <li>Kontakt oss på e-post (vi sender manuelt)</li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">5. Angrerett – viktig!</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              I henhold til norsk lov § 22 n (angrerettloven) har du <strong>IKKE angrerett</strong> på
              digitale produkter som er levert umiddelbart:
            </p>
            <blockquote className="border-l-4 border-[#D97B66] bg-[#F9F8F6] pl-5 pr-4 py-4 mt-4 italic text-[#4A4F4A] leading-relaxed rounded-r-xl">
              «For avtaler om levering av digitalt innhold som ikke leveres på et fysisk medium,
              bortfaller angreretten når leveringen har startet med forbrukerens forhåndssamtykke.»
            </blockquote>
            <p className="text-[#4A4F4A] leading-relaxed mt-4">
              <strong>Ved kjøp hos Småstund:</strong>
              <br />
              Når du fullfører kjøpet, samtykker du til umiddelbar levering av PDF-filene. Du mister da
              angreretten fordi produktene er digitale og kan kopieres.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">6. Reklamasjon</h2>
            <p className="text-[#4A4F4A] leading-relaxed mb-3">
              Hvis PDF-en er defekt (kan ikke åpnes) eller innholdet ikke stemmer med beskrivelsen:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A]">
              <li>Kontakt oss innen 14 dager</li>
              <li>Vi sender en ny PDF eller refunderer beløpet</li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">7. Ansvarsbegrensning</h2>
            <p className="text-[#4A4F4A] leading-relaxed mb-3">Småstund er ikke ansvarlig for:</p>
            <ul className="list-disc pl-6 space-y-2 text-[#4A4F4A]">
              <li>Tekniske problemer hos din e-postleverandør</li>
              <li>At du ikke kan skrive ut PDF-en (manglende skriver)</li>
              <li>Skade som følge av feil bruk av filene</li>
            </ul>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">8. Personvern</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              Se vår separate{" "}
              <Link to="/personvern" className="text-[#D97B66] underline">personvernerklæring</Link>.
            </p>
          </div>

          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-medium mb-4">9. Tvisteløsning</h2>
            <p className="text-[#4A4F4A] leading-relaxed">
              Eventuelle tvister løses i henhold til norsk lov. Vi oppfordrer til å kontakte oss før du
              går videre til Forbrukertilsynet.
            </p>
          </div>

        </div>

        {/* Back link */}
        <div className="mt-16 pt-10 border-t border-[#E5E2DC]">
          <Link
            to="/"
            data-testid="vilkar-back-home"
            className="inline-flex items-center gap-2 text-[#D97B66] hover:text-[#C66A55] font-medium"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Tilbake til forsiden
          </Link>
        </div>
      </section>
    </div>
  );
}
